/**
 * Structured clause rows for the view builder. A row is a small, typed shape
 * the pickers edit; `rowText` RENDERS it to the exact clause source line the
 * draft projects and the save writes — the text stays the single persisted
 * truth, so structured editing can never drift from what lands in `.arch`.
 * A `raw` row is the escape hatch for a whole CLAUSE the rows can't hold; a
 * selection clause's selector is a `SelTerm` tree with its own per-term `raw`
 * leaf, so an unmodellable fragment degrades alone.
 */

/** A node atom of the simple-selector builder (SELECTORS.md §2 subset). */
export type SelectorAtom =
  | { readonly kind: "all" } // `*`
  | { readonly kind: "type"; readonly name: string } // `service`
  | { readonly kind: "element"; readonly path: string } // `Platform.Auth`
  | { readonly kind: "aspect"; readonly key: string; readonly value?: string }; // `@@team` / `@@team:"Ops"`

/** A `color:` render value: colour every match by an aspect key
 *  (`colorize(@@key)` categorical, `heat(@@key)` numeric gradient) or paint one
 *  flat swatch (`"#f43"`). The `css(@field)` cast + computed exprs stay `raw`. */
export type StyleColorValue =
  | { readonly kind: "colorize"; readonly key: string }
  | { readonly kind: "heat"; readonly key: string }
  | { readonly kind: "hex"; readonly value: string };

/** A widget-leaf RHS value. The parser's token kind is PRESERVED — `identifier`
 *  (bareword, `widget.icon: shield`) vs `string` (quoted, `widget.badge: "PII"`)
 *  vs a `getter` (`@@security.zone`) — so re-emit never bare-words a spaced
 *  string or quotes an icon ident (a round-trip corruption trap). */
export type StyleValue =
  | { readonly kind: "ident"; readonly text: string } // bareword — widget.icon: shield
  | { readonly kind: "string"; readonly text: string } // quoted — widget.badge: "PII"
  | { readonly kind: "getter"; readonly getter: string }; // @@security.zone / @name (written form)

/** One member of a `style <selector> { … }` body. `color` + `widget.*` are the
 *  rewrites that actually paint (VIEWS §5); `rename` retitles; `use` applies a
 *  bundle; `width` is the edge stroke width (edge/policy targets only — a node
 *  paints no width); `pin` is a pass-through (drag authors it — kept + re-emitted,
 *  but no editor) so a `pin`+colour body stays structured instead of degrading to raw. */
export type StyleMemberRow =
  | { readonly kind: "color"; readonly value: StyleColorValue }
  | { readonly kind: "widget"; readonly prop: string; readonly value: StyleValue } // widget.<prop>: …
  | { readonly kind: "rename"; readonly label: string }
  | { readonly kind: "use"; readonly bundle: string }
  | { readonly kind: "width"; readonly px: number } // edge/policy stroke width — width: 3
  | { readonly kind: "pin"; readonly x: number; readonly y: number }; // pass-through, no editor

/** An arrow operator (`selector-ast.ts` `ArrowOp`). A `style` EDGE target
 *  authors `>` only — `>>`/`<>` round-trip there as `raw` until a render proof
 *  shows they resolve to an EDGE set (see the Phase 2 plan §3.1). A selection
 *  term's edge offers all three. */
export type ArrowOp = ">" | ">>" | "<>";

/** The target of a `style … { … }` rule. A **node** selector (the Phase 1 reach —
 *  colour + widget + rename + use), an **edge** set `A > B` (colour + width), or a
 *  **policy** violation set `violating <P>` (colour + width). Edge + policy target
 *  EDGES, so their member set is `{colour(hex), width}` only (VIEWS §5; engine
 *  `EdgeStyle` carries color + width). */
export type StyleTarget =
  | { readonly kind: "node"; readonly atom: SelectorAtom; readonly refinements?: readonly Refinement[] }
  | { readonly kind: "edge"; readonly from: SelectorAtom; readonly op: ArrowOp; readonly to: SelectorAtom }
  | { readonly kind: "policy"; readonly policy: string };

/** A conjunction refinement `and`-chained onto a STYLE rule's base node target
 *  (SELECTORS §4): intersect another atom (`andAtom` — e.g. an aspect filter),
 *  exclude one (`notAtom`), scope to a container (`in <ElemRef>`), or a raw
 *  predicate expression (`where (<expr>)`). The base union is parenthesised on
 *  emit when it carries refinements (precedence `or` < `and`). A style rule has
 *  one subject, so it keeps this bounded shape; the SELECTION clauses compose
 *  freely through `SelTerm`. */
export type Refinement =
  | { readonly kind: "andAtom"; readonly atom: SelectorAtom }
  | { readonly kind: "notAtom"; readonly atom: SelectorAtom }
  | { readonly kind: "in"; readonly scope: string; readonly depth?: string }
  | { readonly kind: "where"; readonly expr: string };

export const REFINEMENT_KINDS = ["andAtom", "notAtom", "in", "where"] as const;
export type RefinementKind = (typeof REFINEMENT_KINDS)[number];

/** An extraction head — the one sort conversion in the language, edge set →
 *  node set (SELECTORS.md §6). */
export const EXTRACTION_HEADS = ["sources", "targets", "nodes", "owners"] as const;
export type ExtractionHead = (typeof EXTRACTION_HEADS)[number];

/**
 * A selector as a TREE, not a base union plus a flat `and` tail — the shape the
 * language actually has (SELECTORS.md §4: `not` binds tighter than `and`, which
 * binds tighter than `or`, and any term may be a group). This is what lets the
 * builder express `(a or b) and not (c or d)` instead of only
 * `base or base and ref and ref …`.
 *
 * Every leaf carries its slot in the WRITTEN form the emitter needs, so a term
 * is renderable in isolation and an incomplete one renders `null` (skipped, not
 * an error) exactly like an incomplete row. `raw` is the per-TERM escape hatch:
 * a fragment the pickers can't model no longer forces the whole clause to
 * degrade — it degrades to one leaf, and its siblings stay structured.
 */
export type SelTerm =
  | { readonly kind: "atom"; readonly atom: SelectorAtom }
  // in <Module | Space | Process> [depth N] — `depth` clips the descent to N
  // containment levels below the scope (empty = unbounded).
  | { readonly kind: "in"; readonly scope: string; readonly depth?: string }
  | { readonly kind: "depth"; readonly depth: string } // depth N — from the model root
  | { readonly kind: "on"; readonly plane: string } // on <plane>
  | { readonly kind: "where"; readonly expr: string } // where (<expr>)
  | { readonly kind: "violating"; readonly policy: string } // violating <P>
  | { readonly kind: "outside" } // outside — the not-selected set
  | { readonly kind: "edge"; readonly from: SelTerm; readonly op: ArrowOp; readonly within: string; readonly to: SelTerm }
  | { readonly kind: "extract"; readonly head: ExtractionHead; readonly inner: SelTerm }
  | { readonly kind: "not"; readonly term: SelTerm }
  | { readonly kind: "and"; readonly terms: readonly SelTerm[] }
  | { readonly kind: "or"; readonly terms: readonly SelTerm[] }
  | { readonly kind: "raw"; readonly text: string };

export const SEL_TERM_KINDS = [
  "atom",
  "in",
  "depth",
  "on",
  "where",
  "violating",
  "outside",
  "edge",
  "extract",
  "not",
  "and",
  "or",
  "raw",
] as const;
export type SelTermKind = (typeof SEL_TERM_KINDS)[number];

/** A `flow` clause's render mode (VIEWS.md §6): plain walkthrough, `sequence`,
 *  or `bpmn` (the one mode whose lane/pool body is legal). */
export type FlowMode = "plain" | "sequence" | "bpmn";

/** A lane/pool entry of a `flow … bpmn { … }` body (bpmn-lanes-pools §1). Each
 *  shape mirrors one clause form the flow-body parser accepts. */
export type FlowBodyRow =
  | { readonly kind: "lane-expand"; readonly selector: SelectorAtom } // `lane <selector>`
  | { readonly kind: "lane-merge"; readonly title: string; readonly selector: SelectorAtom } // `lane "T": <sel>`
  | { readonly kind: "lane-by"; readonly key: string } // `lane by @@key`
  | { readonly kind: "pool-ref"; readonly ref: string } // `pool <ElementRef>`
  | { readonly kind: "pool-merge"; readonly title: string; readonly selector: SelectorAtom } // `pool "T": <sel>`
  | { readonly kind: "pool-by"; readonly key: string }; // `pool by @@key`

export const FLOW_BODY_KINDS = [
  "lane-expand",
  "lane-merge",
  "lane-by",
  "pool-ref",
  "pool-merge",
  "pool-by",
] as const;
export type FlowBodyKind = (typeof FLOW_BODY_KINDS)[number];

/** One `column <getter> ["Title"]` of a `table` body — a getter column (the
 *  editable case; calculated `"Title": expr` columns are the `raw` escape
 *  hatch). `getter` is stored in its written form (`@name`, `@@team`). */
export interface TableColumnRow {
  readonly getter: string;
  readonly title: string;
}

/** A `matrix` body's one ordering strategy (VIEWS §6). `none` = default key
 *  order; `aspect` reads `orderKey`. */
export type MatrixOrder = "cluster" | "layer" | "aspect" | "none";

/** A scrub point of the `at` clause: `now`, or any timeline marker in its
 *  WRITTEN form (`v3`, `"2026-Q3"` — quoted when it would not lex as one
 *  token). Mirrors the engine's `ScrubPoint`. */
export type ScrubPointRow =
  | { readonly kind: "now" }
  | { readonly kind: "marker"; readonly text: string };

export type ClauseRow =
  // The selection clauses carry a whole selector TREE (`SelTerm`), so the
  // builder composes the real algebra — nested `and`/`or`/`not`, arrows,
  // extraction — rather than one union plus a flat `and` tail.
  | { readonly kind: "show" | "hide" | "focus"; readonly sel: SelTerm }
  // `group by @@key` groups by aspect VALUE; `scope` switches it to CONTAINMENT
  // (`group by in <scope>`), where the selector names the containers and each
  // becomes a frame around the shown items inside it (VIEWS §4).
  | { readonly kind: "groupBy"; readonly key: string; readonly scope?: string }
  // `at` — the view's timeline reading point; `compare` makes it the `a..b`
  // roadmap delta (spec §9.1). Paints only, never selects.
  | { readonly kind: "at"; readonly at: ScrubPointRow; readonly compare?: ScrubPointRow }
  | { readonly kind: "on"; readonly plane: string }
  // `color` is the one-tap aspect-colour shortcut — the HUD "colour by" pill's
  // clause form (`style * { color: colorize(@@key) }`).
  | { readonly kind: "color"; readonly key: string }
  // `style` is the general rewrite: a target (node / edge / policy) + an ordered
  // list of style members (colour / widget props / rename / use / width / pin).
  | { readonly kind: "style"; readonly selector: StyleTarget; readonly members: readonly StyleMemberRow[] }
  // `flow` picks a process + render mode; `bpmn` mode carries lane/pool rows.
  | { readonly kind: "flow"; readonly ref: string; readonly mode: FlowMode; readonly body: readonly FlowBodyRow[] }
  // `table` — getter columns + an optional sort getter.
  | { readonly kind: "table"; readonly columns: readonly TableColumnRow[]; readonly sortBy: string }
  // `matrix` — square DSM: optional grouped axis + one ordering strategy.
  | { readonly kind: "matrix"; readonly axis: string; readonly order: MatrixOrder; readonly orderKey: string }
  // `grid` — cross-tab: row/col group getters + an optional colour getter.
  | { readonly kind: "grid"; readonly rows: string; readonly cols: string; readonly color: string }
  // `index` — catalog: an optional sort getter + an optional fixed column count
  //  (stored as text, empty = auto-fit).
  | { readonly kind: "index"; readonly sortBy: string; readonly columns: string }
  | { readonly kind: "raw"; readonly text: string };

export const ROW_KINDS = ["show", "hide", "focus", "groupBy", "at", "on", "color", "style", "flow", "table", "matrix", "grid", "index", "raw"] as const;
export type RowKind = (typeof ROW_KINDS)[number];

/** The view configurator's per-tab state (persisted per tab, across reloads):
 *  the structured clause rows being composed, the model view being MODIFIED
 *  (`baseViewName` — null = a new/scratch config), and whether the user has
 *  diverged from the base yet (`dirty` — gates the live board projection so a
 *  pristine config never hijacks the view or its WYSIWYG write-back rail).
 *  Structured rows are stored verbatim, so an in-progress incomplete row
 *  survives a reload. */
export interface ViewConfig {
  readonly rows: readonly ClauseRow[];
  /** The model view being MODIFIED (its name is locked). null = a NEW view. */
  readonly baseViewName: string | null;
  /** The typed name for a NEW view (ignored when `baseViewName` is set — the base
   *  view's name is authoritative). Persists per tab so a half-typed name
   *  survives a reload. */
  readonly name: string;
  readonly dirty: boolean;
  /** The base view's knob-decl row texts AS SEEDED (trimmed) — the baseline
   *  the modify-save compares against to detect a USER knob edit. Comparing
   *  against a live re-derivation instead false-positived when the knob decl
   *  changed underneath the seeded draft (a concurrent text edit), refusing a
   *  clause-only save. Absent on legacy persisted configs (falls back to the
   *  re-derivation). */
  readonly seedKnobs?: readonly string[];
}

/** Built-in element kinds usable as type atoms (SELECTORS.md §2). */
export const BUILTIN_KIND_ATOMS = ["module", "interface", "surface", "process", "subprocess", "space"] as const;

/**
 * A user string → a `.arch` string literal.
 *
 * The lexer un-escapes `\"` and `\\`, so the emitter has to put them back:
 * interpolating a raw title straight into `"…"` meant one `"` in a column
 * heading or lane name wrote source that no longer parses.
 */
export function quoted(text: string): string {
  return `"${text.trim().replace(/([\\"])/g, "\\$1")}"`;
}

export function atomText(a: SelectorAtom): string {
  switch (a.kind) {
    case "all":
      return "*";
    case "type":
      return a.name;
    case "element":
      return a.path;
    case "aspect":
      // The value is stored in its WRITTEN form (`"Ops"` quoted classification
      // vs `Euro.DC1` bare place ref) — glued per the §2 valued-atom rule.
      return a.value ? `@@${a.key}:${a.value}` : `@@${a.key}`;
  }
}

export function atomLabel(a: SelectorAtom): string {
  return atomText(a);
}

/** An atom is complete once its picked slot is filled (`*` always is). */
export function atomComplete(a: SelectorAtom): boolean {
  switch (a.kind) {
    case "all":
      return true;
    case "type":
      return a.name.length > 0;
    case "element":
      return a.path.length > 0;
    case "aspect":
      return a.key.length > 0;
  }
}

/** One refinement → its `and`-term text; null = incomplete (skipped). */
export function refText(r: Refinement): string | null {
  switch (r.kind) {
    case "andAtom":
      return atomComplete(r.atom) ? atomText(r.atom) : null;
    case "notAtom":
      return atomComplete(r.atom) ? `not ${atomText(r.atom)}` : null;
    case "in":
      return r.scope.trim() ? `in ${r.scope.trim()}${boundText(r.depth, "depth")}` : null;
    case "where":
      return r.expr.trim() ? `where (${r.expr.trim()})` : null;
  }
}

// ---- selector terms ---------------------------------------------------------

/**
 * Binding tightness, so a child parenthesises exactly when the grammar needs
 * it: `or` < `and` < `not` < an arrow < a primary.
 *
 * The arrow's place is set by the parser, not by intuition. `parseSelTerm`
 * builds a chain out of `parseSelPrimary()` SIDES, so `>` binds tighter than
 * every combinator: `A > B and C` is `(A > B) and C`, and `A or B > C` is
 * `A or (B > C)`. Reading it the other way round — as the loosest operator —
 * dropped the parens that `(A or B) > C` cannot survive without, and the
 * re-parse silently produced a different view.
 */
const EDGE_PRECEDENCE = 3.5;

function termPrecedence(t: SelTerm): number {
  switch (t.kind) {
    case "or":
      return 1;
    case "and":
      return 2;
    case "not":
      return 3;
    case "edge":
      return EDGE_PRECEDENCE;
    default:
      return 4;
  }
}

/** A child's text, parenthesised iff it binds looser than its parent slot. */
function childText(t: SelTerm, parentPrecedence: number): string | null {
  const text = selTermText(t);
  if (text === null) return null;
  return termPrecedence(t) < parentPrecedence ? `(${text})` : text;
}

/** A `within N` / `depth N` numeric suffix — empty (or absent) = unbounded. */
function boundText(v: string | undefined, kw: "within" | "depth"): string {
  return v && v.trim() ? ` ${kw} ${v.trim()}` : "";
}

/**
 * A term → its selector source, or null when incomplete (an unfilled slot, or a
 * group whose every member is incomplete) — the same "skip it, don't error"
 * contract `rowText` has. A group with ONE complete member emits that member
 * bare: `and` of one is not a conjunction.
 */
export function selTermText(t: SelTerm): string | null {
  switch (t.kind) {
    case "atom":
      return atomComplete(t.atom) ? atomText(t.atom) : null;
    case "in":
      return t.scope.trim() ? `in ${t.scope.trim()}${boundText(t.depth, "depth")}` : null;
    case "depth":
      return t.depth.trim() ? `depth ${t.depth.trim()}` : null;
    case "on":
      return t.plane.trim() ? `on ${t.plane.trim()}` : null;
    case "where":
      return t.expr.trim() ? `where (${t.expr.trim()})` : null;
    case "violating":
      return t.policy.trim() ? `violating ${t.policy.trim()}` : null;
    case "outside":
      return "outside";
    case "edge": {
      // Both sides are PRIMARIES in the grammar, so anything that is not one
      // has to be wrapped — including a `not`, which binds looser than the
      // arrow.
      const from = childText(t.from, 4);
      const to = childText(t.to, 4);
      if (from === null || to === null) return null;
      // `within` is a hop suffix and only `>>`/`<>` take one — the parser
      // rejects `A > B within 2` outright, pointing at `>>`.
      const within = t.op !== ">" ? boundText(t.within, "within") : "";
      return `${from} ${t.op} ${to}${within}`;
    }
    case "extract": {
      // The inner set is always parenthesised — `sources of (…)` is the written
      // form, and the parens restart the selector grammar (SELECTORS.md §1).
      const inner = selTermText(t.inner);
      return inner === null ? null : `${t.head} of (${inner})`;
    }
    case "not": {
      const inner = childText(t.term, 3);
      return inner === null ? null : `not ${inner}`;
    }
    case "and":
    case "or": {
      const prec = t.kind === "and" ? 2 : 1;
      const parts = t.terms.map((x) => childText(x, prec)).filter((x): x is string => x !== null);
      if (parts.length === 0) return null;
      if (parts.length === 1) return parts[0]!;
      return parts.join(t.kind === "and" ? " and " : " or ");
    }
    case "raw":
      return t.text.trim() || null;
  }
}

/** A term the emitter would render — the builder's "this row counts" test. */
export const selTermComplete = (t: SelTerm): boolean => selTermText(t) !== null;

/** Compose a base selector text + a refinement list into an `and`-conjunction.
 *  The base is parenthesised iff it is a union (`baseIsUnion`) AND carries
 *  refinements (precedence `or` < `and`); an empty base (a pure-predicate
 *  selector) is omitted. Empty base + no complete refinements → null. A
 *  refinement-free base re-emits byte-identically (the zero-regression guarantee). */
export function refineText(base: string, baseIsUnion: boolean, refinements: readonly Refinement[] = []): string | null {
  const refs = refinements.map(refText).filter((t): t is string => t !== null);
  if (refs.length === 0) return base || null;
  const group = base ? (baseIsUnion ? `(${base})` : base) : null;
  const parts = group ? [group, ...refs] : refs;
  return parts.length ? parts.join(" and ") : null;
}

// ---- structural row validation ---------------------------------------------
// THE shape check for a row arriving from an untrusted source (persisted
// blobs). Lives beside the types so a new variant/field updates both in one
// place. Emitter-probing was tried instead (run `rowText`, drop on throw) and
// failed: the emitter is LENIENT about fields the row's current state doesn't
// emit (an atom with no payload joins to "" → "incomplete, keep"; a plain-mode
// flow never touches `body`) while the popover editors dereference those same
// fields strictly — so mode-gated / summarize-path shapes crashed after
// passing the probe (bugloop r33). Structural, exhaustive, unconditional.

const isStr = (v: unknown): v is string => typeof v === "string";
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;
const isArr = (v: unknown, ok: (e: unknown) => boolean): boolean =>
  Array.isArray(v) && v.every(ok);

function isSelectorAtom(v: unknown): v is SelectorAtom {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "all":
      return true;
    case "type":
      return isStr(v.name);
    case "element":
      return isStr(v.path);
    case "aspect":
      return isStr(v.key) && (v.value === undefined || isStr(v.value));
    default:
      return false;
  }
}

function isRefinement(v: unknown): v is Refinement {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "andAtom":
    case "notAtom":
      return isSelectorAtom(v.atom);
    case "in":
      return isStr(v.scope) && (v.depth === undefined || isStr(v.depth));
    case "where":
      return isStr(v.expr);
    default:
      return false;
  }
}

function isSelTerm(v: unknown): v is SelTerm {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "atom":
      return isSelectorAtom(v.atom);
    case "in":
      return isStr(v.scope) && (v.depth === undefined || isStr(v.depth));
    case "depth":
      return isStr(v.depth);
    case "on":
      return isStr(v.plane);
    case "where":
      return isStr(v.expr);
    case "violating":
      return isStr(v.policy);
    case "outside":
      return true;
    case "edge":
      return (
        isSelTerm(v.from) &&
        isSelTerm(v.to) &&
        isStr(v.within) &&
        (v.op === ">" || v.op === ">>" || v.op === "<>")
      );
    case "extract":
      return (EXTRACTION_HEADS as readonly unknown[]).includes(v.head) && isSelTerm(v.inner);
    case "not":
      return isSelTerm(v.term);
    case "and":
    case "or":
      return isArr(v.terms, isSelTerm);
    case "raw":
      return isStr(v.text);
    default:
      return false;
  }
}

function isScrubPointRow(v: unknown): v is ScrubPointRow {
  if (!isObj(v)) return false;
  return v.kind === "now" || (v.kind === "marker" && isStr(v.text));
}

function isStyleColorValue(v: unknown): v is StyleColorValue {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "colorize":
    case "heat":
      return isStr(v.key);
    case "hex":
      return isStr(v.value);
    default:
      return false;
  }
}

function isStyleValue(v: unknown): v is StyleValue {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "ident":
    case "string":
      return isStr(v.text);
    case "getter":
      return isStr(v.getter);
    default:
      return false;
  }
}

function isStyleMemberRow(v: unknown): v is StyleMemberRow {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "color":
      return isStyleColorValue(v.value);
    case "widget":
      return isStr(v.prop) && isStyleValue(v.value);
    case "rename":
      return isStr(v.label);
    case "use":
      return isStr(v.bundle);
    case "width":
      return isNum(v.px);
    case "pin":
      return isNum(v.x) && isNum(v.y);
    default:
      return false;
  }
}

function isStyleTarget(v: unknown): v is StyleTarget {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "node":
      return (
        isSelectorAtom(v.atom) &&
        (v.refinements === undefined || isArr(v.refinements, isRefinement))
      );
    case "edge":
      return (
        isSelectorAtom(v.from) &&
        isSelectorAtom(v.to) &&
        (v.op === ">" || v.op === ">>" || v.op === "<>")
      );
    case "policy":
      return isStr(v.policy);
    default:
      return false;
  }
}

function isFlowBodyRow(v: unknown): v is FlowBodyRow {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "lane-expand":
      return isSelectorAtom(v.selector);
    case "lane-merge":
    case "pool-merge":
      return isStr(v.title) && isSelectorAtom(v.selector);
    case "lane-by":
    case "pool-by":
      return isStr(v.key);
    case "pool-ref":
      return isStr(v.ref);
    default:
      return false;
  }
}

/** Structural type guard for one persisted clause row — every field ANY
 *  consumer (emitter, summary, popover editors) dereferences is checked,
 *  including mode-gated slices (a plain-mode flow's `body` still must be an
 *  array: a mode flip makes it live). */
export function isClauseRow(v: unknown): v is ClauseRow {
  if (!isObj(v)) return false;
  switch (v.kind) {
    case "show":
    case "hide":
    case "focus":
      return isSelTerm(v.sel);
    case "groupBy":
      return isStr(v.key) && (v.scope === undefined || isStr(v.scope));
    case "at":
      return isScrubPointRow(v.at) && (v.compare === undefined || isScrubPointRow(v.compare));
    case "on":
      return isStr(v.plane);
    case "color":
      return isStr(v.key);
    case "style":
      return isStyleTarget(v.selector) && isArr(v.members, isStyleMemberRow);
    case "flow":
      return (
        isStr(v.ref) &&
        (v.mode === "plain" || v.mode === "sequence" || v.mode === "bpmn") &&
        isArr(v.body, isFlowBodyRow)
      );
    case "table":
      return (
        isArr(v.columns, (c) => isObj(c) && isStr(c.getter) && isStr(c.title)) &&
        isStr(v.sortBy)
      );
    case "matrix":
      return (
        isStr(v.axis) &&
        (v.order === "cluster" || v.order === "layer" || v.order === "aspect" || v.order === "none") &&
        isStr(v.orderKey)
      );
    case "grid":
      return isStr(v.rows) && isStr(v.cols) && isStr(v.color);
    case "index":
      return isStr(v.sortBy) && isStr(v.columns);
    case "raw":
      return isStr(v.text);
    default:
      return false;
  }
}

/** A scrub point → its written form; null = incomplete. A marker is stored as
 *  written, so one needing quotes (`"2026-Q3"`) already carries them. */
export function scrubPointText(p: ScrubPointRow): string | null {
  return p.kind === "now" ? "now" : p.text.trim() || null;
}

/** Render a row to its clause source line; null = incomplete (not an error —
 *  the builder just skips it from apply/save and flags the row). */
export function rowText(r: ClauseRow): string | null {
  switch (r.kind) {
    case "show":
    case "hide":
    case "focus": {
      const body = selTermText(r.sel);
      return body ? `${r.kind} ${body}` : null;
    }
    case "groupBy":
      if (r.scope !== undefined) return r.scope.trim() ? `group by in ${r.scope.trim()}` : null;
      return r.key ? `group by @@${r.key}` : null;
    case "at": {
      const head = scrubPointText(r.at);
      if (!head) return null;
      const tail = r.compare ? scrubPointText(r.compare) : null;
      return tail ? `at ${head}..${tail}` : `at ${head}`;
    }
    case "on":
      return r.plane ? `on ${r.plane}` : null;
    case "color":
      return r.key ? `style * { color: colorize(@@${r.key}) }` : null;
    case "style": {
      const target = styleTargetText(r.selector);
      if (target === null) return null;
      // Members emit in AUTHOR order (meaningful — engine last-wins per leaf).
      const members = r.members.map(styleMemberText).filter((t): t is string => t !== null);
      return members.length ? `style ${target} { ${members.join("; ")} }` : null;
    }
    case "flow": {
      if (!r.ref.trim()) return null;
      const head = r.mode === "plain" ? `flow ${r.ref.trim()}` : `flow ${r.ref.trim()} ${r.mode}`;
      // The lane/pool body is legal only on bpmn; other modes emit the bare head.
      if (r.mode !== "bpmn") return head;
      const body = r.body.map(flowBodyRowText).filter((t): t is string => t !== null);
      return body.length ? `${head} { ${body.join("; ")} }` : head;
    }
    case "table": {
      const cols = r.columns
        .filter((c) => c.getter.trim())
        .map((c) => (c.title.trim() ? `column ${c.getter.trim()} ${quoted(c.title)}` : `column ${c.getter.trim()}`));
      if (cols.length === 0) return null;
      const body = [...cols, ...(r.sortBy.trim() ? [`sort by ${r.sortBy.trim()}`] : [])];
      return `table { ${body.join("; ")} }`;
    }
    case "matrix": {
      const parts: string[] = [];
      if (r.axis) parts.push(`axis @@${r.axis}`);
      if (r.order === "cluster") parts.push("cluster");
      else if (r.order === "layer") parts.push("order by layer");
      else if (r.order === "aspect" && r.orderKey) parts.push(`order by @@${r.orderKey}`);
      // An empty body is the default square DSM; keep it valid + non-empty.
      return parts.length ? `matrix { ${parts.join("; ")} }` : "matrix { cluster }";
    }
    case "grid": {
      const parts: string[] = [];
      if (r.rows) parts.push(`rows @@${r.rows}`);
      if (r.cols) parts.push(`cols @@${r.cols}`);
      if (r.color) parts.push(`color @@${r.color}`);
      // A bare `grid` is valid — both axes default to the module set — so an
      // unconfigured grid still SWITCHES the surface (and reveals the axis
      // pickers) instead of silently emitting nothing and leaving the board.
      return parts.length ? `grid { ${parts.join("; ")} }` : "grid";
    }
    case "index": {
      const parts: string[] = [];
      if (r.sortBy.trim()) parts.push(`sort by ${r.sortBy.trim()}`);
      const n = Number.parseInt(r.columns.trim(), 10);
      if (Number.isFinite(n) && n > 0) parts.push(`columns ${n}`);
      // A bare `index` is valid — default `@name` order, auto-fit columns — so an
      // unconfigured catalog still SWITCHES the surface (and reveals the pickers)
      // instead of emitting nothing and leaving the board.
      return parts.length ? `index { ${parts.join("; ")} }` : "index";
    }
    case "raw":
      return r.text.trim() ? r.text : null;
  }
}

/** Render one style-body member to its clause text; null = incomplete (skipped). */
export function styleMemberText(m: StyleMemberRow): string | null {
  switch (m.kind) {
    case "color": {
      const v = styleColorText(m.value);
      return v ? `color: ${v}` : null;
    }
    case "widget": {
      const v = styleValueText(m.value);
      return m.prop.trim() && v ? `widget.${m.prop.trim()}: ${v}` : null;
    }
    case "rename":
      return m.label.trim() ? `rename ${quoted(m.label)}` : null;
    case "use":
      return m.bundle.trim() ? `use ${m.bundle.trim()}` : null;
    case "width":
      // Bare integer (never quoted) — engine reads a numeric `width:` overlay leaf.
      return Number.isFinite(m.px) ? `width: ${Math.round(m.px)}` : null;
    case "pin":
      // Pass-through: drag authored it; re-emit verbatim (signed literals legal).
      return `pin ${m.x}, ${m.y}`;
  }
}

/** Render a `style` target to its selector text; null = incomplete (skipped). A
 *  NODE atom (`*`/type/element/aspect), an EDGE `from op to`, or a `violating <P>`
 *  policy set. Edge/policy paint edges (colour + width). */
export function styleTargetText(t: StyleTarget): string | null {
  switch (t.kind) {
    case "node":
      // A single base atom + optional refinements (a union style base → raw).
      return atomComplete(t.atom) ? refineText(atomText(t.atom), false, t.refinements ?? []) : null;
    case "edge":
      return atomComplete(t.from) && atomComplete(t.to)
        ? `${atomText(t.from)} ${t.op} ${atomText(t.to)}`
        : null;
    case "policy":
      return t.policy.trim() ? `violating ${t.policy.trim()}` : null;
  }
}

/** A style target is complete once its picked slots are filled. */
export function styleTargetComplete(t: StyleTarget): boolean {
  return styleTargetText(t) !== null;
}

/** The default flat edge/policy colour (a hex the swatch can display). */
export const EDGE_DEFAULT_HEX = "#f43f5e";

/** A fresh style target of the given kind (edge defaults to a `>` hop). */
export function seedStyleTarget(kind: StyleTarget["kind"]): StyleTarget {
  switch (kind) {
    case "node":
      return { kind: "node", atom: { kind: "all" } };
    case "edge":
      return { kind: "edge", from: { kind: "all" }, op: ">", to: { kind: "all" } };
    case "policy":
      return { kind: "policy", policy: "" };
  }
}

/** Coerce a style body to the member set its target can paint. A node target keeps
 *  everything; an edge/policy target keeps only `colour` (forced to a **hex** swatch —
 *  `colorize`/`heat` emit a `colorNeedsCast` diagnostic on an edge and paint nothing)
 *  + `width`, dropping node-only members (widget/rename/use/pin). Guarantees a
 *  non-empty body so a mode switch never blanks the rule. */
export function coerceStyleMembersForTarget(
  members: readonly StyleMemberRow[],
  targetKind: StyleTarget["kind"],
): StyleMemberRow[] {
  if (targetKind === "node") return [...members];
  const out: StyleMemberRow[] = [];
  for (const m of members) {
    if (m.kind === "width") out.push(m);
    else if (m.kind === "color")
      out.push(m.value.kind === "hex" ? m : { kind: "color", value: { kind: "hex", value: EDGE_DEFAULT_HEX } });
  }
  if (!out.some((m) => m.kind === "color")) out.unshift({ kind: "color", value: { kind: "hex", value: EDGE_DEFAULT_HEX } });
  return out;
}

/** A `color:` value → its cast/literal text; null = incomplete. */
export function styleColorText(v: StyleColorValue): string | null {
  switch (v.kind) {
    case "colorize":
      return v.key ? `colorize(@@${v.key})` : null;
    case "heat":
      return v.key ? `heat(@@${v.key})` : null;
    case "hex":
      return v.value.trim() ? quoted(v.value) : null;
  }
}

/** A widget-leaf value → text, QUOTED BY TOKEN KIND: `ident` bareword, `string`
 *  quoted, `getter` verbatim. Never guess quoting from the prop name. */
export function styleValueText(v: StyleValue): string | null {
  switch (v.kind) {
    case "ident":
      return v.text.trim() ? v.text.trim() : null;
    case "string":
      return v.text.trim() ? quoted(v.text) : null;
    case "getter":
      return v.getter.trim() ? v.getter.trim() : null;
  }
}

/** Render one lane/pool body row to its clause text; null = incomplete. */
export function flowBodyRowText(b: FlowBodyRow): string | null {
  switch (b.kind) {
    case "lane-expand":
      return atomComplete(b.selector) ? `lane ${atomText(b.selector)}` : null;
    case "lane-merge":
      return b.title.trim() && atomComplete(b.selector)
        ? `lane ${quoted(b.title)}: ${atomText(b.selector)}`
        : null;
    case "lane-by":
      return b.key ? `lane by @@${b.key}` : null;
    case "pool-ref":
      return b.ref.trim() ? `pool ${b.ref.trim()}` : null;
    case "pool-merge":
      return b.title.trim() && atomComplete(b.selector)
        ? `pool ${quoted(b.title)}: ${atomText(b.selector)}`
        : null;
    case "pool-by":
      return b.key ? `pool by @@${b.key}` : null;
  }
}

export function seedRow(kind: RowKind): ClauseRow {
  switch (kind) {
    case "show":
    case "hide":
    case "focus":
      // A fresh selection starts as an empty `or` group: adding a term is the
      // union the old atom-chip list was, and wrapping it is one click away.
      return { kind, sel: { kind: "or", terms: [] } };
    case "groupBy":
      return { kind, key: "" };
    case "at":
      // `now` is always a legal point (it is not a marker), so a fresh row is
      // complete on add and the projection moves the scrubber immediately.
      return { kind, at: { kind: "now" } };
    case "on":
      return { kind, plane: "" };
    case "color":
      return { kind, key: "" };
    case "style":
      // A fresh rule starts as a node target with one (empty) colour member.
      return { kind, selector: { kind: "node", atom: { kind: "all" } }, members: [{ kind: "color", value: { kind: "colorize", key: "" } }] };
    case "flow":
      return { kind, ref: "", mode: "plain", body: [] };
    case "table":
      return { kind, columns: [{ getter: "@name", title: "" }], sortBy: "" };
    case "matrix":
      return { kind, axis: "", order: "cluster", orderKey: "" };
    case "grid":
      return { kind, rows: "", cols: "", color: "" };
    case "index":
      return { kind, sortBy: "", columns: "" };
    case "raw":
      return { kind, text: "" };
  }
}

export function seedRefinement(kind: RefinementKind): Refinement {
  switch (kind) {
    case "andAtom":
      return { kind: "andAtom", atom: { kind: "type", name: "" } };
    case "notAtom":
      return { kind: "notAtom", atom: { kind: "aspect", key: "" } };
    case "in":
      return { kind: "in", scope: "" };
    case "where":
      return { kind: "where", expr: "" };
  }
}

export function seedFlowBodyRow(kind: FlowBodyKind): FlowBodyRow {
  switch (kind) {
    case "lane-expand":
      return { kind, selector: { kind: "all" } };
    case "lane-merge":
      return { kind, title: "", selector: { kind: "all" } };
    case "lane-by":
      return { kind, key: "" };
    case "pool-ref":
      return { kind, ref: "" };
    case "pool-merge":
      return { kind, title: "", selector: { kind: "all" } };
    case "pool-by":
      return { kind, key: "" };
  }
}

/** A raw row that IS a knob header DECL (`knob …` as a whole word — a name
 *  merely starting with "knob", e.g. `knobiness one of: …`, doesn't match).
 *  Knob decls ride the row list as raw rows but are header members, not
 *  clauses, so both the builder's section split and the save's `setKnobs` /
 *  `setClauses` split key on this ONE test. Trim first: a raw row emits the
 *  user's text verbatim, and a leading space must not smuggle one past. */
export const isKnobRowText = (t: string): boolean => /^knob(?!\p{ID_Continue})/u.test(t.trimStart());

/** The default seed: `show *`. */
export function seedShowAll(): ClauseRow {
  return { kind: "show", sel: { kind: "atom", atom: { kind: "all" } } };
}

export function seedSelTerm(kind: SelTermKind): SelTerm {
  switch (kind) {
    case "atom":
      return { kind: "atom", atom: { kind: "type", name: "" } };
    case "in":
      return { kind: "in", scope: "" };
    case "depth":
      return { kind: "depth", depth: "" };
    case "on":
      return { kind: "on", plane: "" };
    case "where":
      return { kind: "where", expr: "" };
    case "violating":
      return { kind: "violating", policy: "" };
    case "outside":
      return { kind: "outside" };
    case "edge":
      return {
        kind: "edge",
        from: { kind: "atom", atom: { kind: "all" } },
        op: ">",
        within: "",
        to: { kind: "atom", atom: { kind: "all" } },
      };
    case "extract":
      return { kind: "extract", head: "targets", inner: seedSelTerm("edge") };
    case "not":
      return { kind: "not", term: { kind: "atom", atom: { kind: "type", name: "" } } };
    case "and":
    case "or":
      return { kind, terms: [] };
    case "raw":
      return { kind: "raw", text: "" };
  }
}

/** Is this term the bare universal atom (`*`)? */
const isAllTerm = (t: SelTerm): boolean => t.kind === "atom" && t.atom.kind === "all";

/** Drop every bare `*` member of a top-level union, collapsing a one-member
 *  group to its member. Returns null when nothing selectable is left. */
function withoutAll(t: SelTerm): SelTerm | null {
  if (isAllTerm(t)) return null;
  if (t.kind !== "or") return t;
  const terms = t.terms.filter((x) => !isAllTerm(x));
  if (terms.length === 0) return null;
  return terms.length === 1 ? terms[0]! : { kind: "or", terms };
}

/** `show *` is the special, selected-by-default clause — and `*` ("everything")
 *  is mutually exclusive with naming specific things to show. So once any show
 *  clause carries a specific atom, every bare `*` is redundant and dropped (a
 *  show clause left empty by that goes too); and when a view that HAD a show is
 *  left with none, `show *` returns as the default. Applied after every builder
 *  row mutation, so the projected/saved text never carries a redundant
 *  `show * or Foo`. Never injects a show into a show-less (e.g. pure `flow`) view. */
export function normalizeShowAll(rows: readonly ClauseRow[]): ClauseRow[] {
  // show / hide / focus share ONE union member (combined `kind`); extract it by
  // its `sel` shape, then gate on `kind === "show"` at runtime.
  type SelectorClause = Extract<ClauseRow, { sel: SelTerm }>;
  const isShow = (r: ClauseRow): r is SelectorClause => r.kind === "show";
  const hadShow = rows.some(isShow);
  // A show is "specific" once its term is anything other than a bare `*` (or a
  // union that is nothing BUT `*`s) — a narrowed `*`, e.g. `* and in Payments`,
  // counts, so a bare `*` sibling beside it is redundant.
  const hasSpecificShow = rows.some((r) => isShow(r) && withoutAll(r.sel) !== null);
  let next: ClauseRow[] = rows.map((r): ClauseRow => {
    if (!isShow(r) || !hasSpecificShow) return r;
    const sel = withoutAll(r.sel);
    return sel ? { ...r, sel } : r; // an all-`*` show is dropped below, not emptied
  });
  // Drop a show left with nothing selectable — but a PURE-PREDICATE show
  // (`show not @@team`) is NOT empty and must survive, else it would be
  // silently replaced by `show *` (a data-loss corruption).
  next = next.filter((r) => !(isShow(r) && (hasSpecificShow ? isAllTerm(r.sel) : false)));
  next = next.filter((r) => !(isShow(r) && !selTermComplete(r.sel) && !isAllTerm(r.sel) && emptyGroup(r.sel)));
  if (hadShow && !next.some(isShow)) next = [seedShowAll(), ...next];
  return next;
}

/** A group with no members at all — the shape a freshly-added, still-unfilled
 *  selection row has. (A group with INCOMPLETE members is being edited and
 *  stays.) */
const emptyGroup = (t: SelTerm): boolean => (t.kind === "and" || t.kind === "or") && t.terms.length === 0;